import EventIntroPopup from "@/components/Event/EventIntroPopup.vue";
import { useEventStore } from "@/event";
import useSocketService from "@/hooks/useSocketService";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import { SkinTypes } from "@/types/ui";
import { storeToRefs } from "pinia";
import { markRaw } from "vue";

/** Whether the root intro may be shown (active event, level met, not maintenance). */
export function isEventIntroEligible(): boolean {
	const eventStore = useEventStore();
	const runtimeStore = useRuntimeStore();
	const { isActive } = storeToRefs(eventStore);

	if (runtimeStore.isMaintenanceMode) return false;
	return isActive.value;
}

function shouldOfferEventIntro(): boolean {
	const eventStore = useEventStore();
	if (!isEventIntroEligible()) return false;
	return !eventStore.introDismissed;
}

/**
 * Schedules the root event intro by setting the event flag.
 * For intro replay inside the event hub modal, see `EventPopup.vue` (`isIntroVisible`).
 *
 * UI only — no lifecycle side effects. `introDismissed` is persisted when the popup closes.
 * Shown once per event while `introDismissed` is false (synced from BE eventUserData on load).
 */
export function tryShowIntroPopup(): boolean {
	const eventStore = useEventStore();

	if (!shouldOfferEventIntro()) {
		eventStore.setEventIntroPopupActive(false);
		return false;
	}

	eventStore.setEventIntroPopupActive(true);
	return true;
}

/**
 * Renders the event intro popup if it was scheduled by {@link tryShowIntroPopup}.
 * Call after the offline income / daily bonus / news stack on session start,
 * or immediately when the event becomes active mid-session.
 */
export function showIntroPopup(): boolean {
	const eventStore = useEventStore();
	const { isIntroPopupActive } = storeToRefs(eventStore);

	if (!isIntroPopupActive.value) return false;

	if (!shouldOfferEventIntro()) {
		eventStore.setEventIntroPopupActive(false);
		return false;
	}

	usePopupStore().showPopup({
		id: PopupDictionary.EVENT_INTRO,
		type: PopupType.EVENT,
		component: markRaw(EventIntroPopup),
		isPaddingless: true,
		skin: SkinTypes.DARK,
		width: 24.5,
		height: "auto",
		hasOverlap: true,
		hasOuterBar: true,
		closeCallback: (): void => {
			eventStore.setIntroDismissed(true);
			eventStore.setEventIntroPopupActive(false);
			void useSocketService().emitSaveRequest({ stores: [] });
		},
	});

	return true;
}
